/**
 * Realtime handler for WhatsApp events pushed over the connection gateway
 * (`whatsapp_message`). Patches the TanStack Query caches so incoming messages
 * appear instantly instead of waiting for the ~4s poll.
 */
import { queryClient } from '@queries/client';
import { whatsappKeys } from './keys';
import type { WhatsappMessage } from './messages';

/** Shape pushed by `whatsapp_service`'s `ConnectionGatewaySink`. */
export interface WhatsappRealtimePayload {
  kind?: 'message' | 'status' | 'connection';
  accountId?: string;
  chatJid?: string;
  senderJid?: string;
  fromMe?: boolean;
  wamid?: string;
  body?: string;
  msgType?: string;
  senderName?: string;
  tsUnixMs?: string;
  status?: string;
  state?: string;
}

export function handleWhatsappMessage(payload: WhatsappRealtimePayload): void {
  const accountId = payload.accountId;
  if (!accountId) return;

  if (payload.kind === 'message' && payload.chatJid && payload.wamid) {
    const chatJid = payload.chatJid;
    const wamid = payload.wamid;
    const key = whatsappKeys.messages(accountId, chatJid);
    queryClient.setQueryData<WhatsappMessage[]>(key, (prev) => {
      const message: WhatsappMessage = {
        chatJid,
        senderJid: payload.senderJid ?? '',
        fromMe: payload.fromMe ?? false,
        wamid,
        body: payload.body ?? '',
        msgType: payload.msgType ?? 'TEXT',
        senderName: payload.senderName ?? '',
        tsUnixMs: payload.tsUnixMs ?? '0',
      };
      if (!prev) return [message];
      if (prev.some((m) => m.wamid === wamid)) return prev; // dedupe optimistic/echo
      return [...prev, message];
    });
    // Bump the conversation list (last message + most-recent-first ordering).
    void queryClient.invalidateQueries({ queryKey: whatsappKeys.chats(accountId) });
  } else if (payload.kind === 'connection') {
    void queryClient.invalidateQueries({ queryKey: whatsappKeys.accounts });
  }
}
